<template>
  <div>
    <!-- Loading State -->
    <v-container
      v-if="loading"
      style="height: 200px;"
    >
      <v-row
        class="fill-height"
        align-content="center"
        justify="center"
      >
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        ></v-progress-circular>
      </v-row>
    </v-container>

    <!-- User Not Found -->
    <v-container
      v-else-if="!userFound"
      style="height: 400px;"
    >
      <v-row
        class="fill-height"
        align-content="center"
        justify="center"
      >
        <v-col
          class="text-subtitle-1 text-center"
          cols="12"
        >
          <v-icon
            size="64"
            class="mb-4"
          >
            {{ mdiAccountOff }}
          </v-icon>
          <div>This user profile is not available.</div>
        </v-col>
      </v-row>
    </v-container>

    <!-- Profile Header -->
    <template v-else>
      <div class="profile-header">
        <v-container class="profile-header-content">
          <div class="profile-header-row">
            <div class="profile-avatar-wrapper">
              <v-avatar
                size="168"
                class="profile-avatar"
              >
                <v-img
                  :src="avatarUrl"
                  :alt="user.name"
                ></v-img>
              </v-avatar>
            </div>
            <div class="profile-info">
              <h1 class="profile-name">
                {{ user.name }}
              </h1>
              <p
                v-if="connectionsCount > 0"
                class="profile-friends-count"
              >
                {{ connectionsCount }} connection{{ connectionsCount !== 1 ? 's' : '' }}
              </p>
              <div
                v-if="connections.length"
                class="profile-friends-avatars"
              >
                <v-avatar
                  v-for="(connection, idx) in connections.slice(0, 8)"
                  :key="`conn-${idx}`"
                  size="32"
                  class="friend-avatar"
                >
                  <v-img
                    :src="getAvatarUrl(connection.avatar) || defaultAvatar"
                    :alt="connection.name"
                  ></v-img>
                </v-avatar>
              </div>
            </div>
            <div class="profile-actions">
              <!-- Connection Button (only show when logged in and not viewing own profile) -->
              <template v-if="isLoggedIn && !isSelf">
                <!-- Not connected -->
                <v-btn
                  v-if="connectionStatus === 'none'"
                  color="primary"
                  class="action-btn"
                  :loading="connectionLoading"
                  @click="sendRequest()"
                >
                  <v-icon left>
                    {{ mdiAccountPlus }}
                  </v-icon>
                  Connect
                </v-btn>

                <!-- Pending outgoing -->
                <v-btn
                  v-else-if="connectionStatus === 'pending' && connectionDirection === 'outgoing'"
                  outlined
                  class="action-btn"
                  :loading="connectionLoading"
                  @click="cancelRequest()"
                >
                  <v-icon left>
                    {{ mdiAccountClock }}
                  </v-icon>
                  Request Sent
                </v-btn>

                <!-- Pending incoming -->
                <div
                  v-else-if="connectionStatus === 'pending' && connectionDirection === 'incoming'"
                  class="d-flex"
                >
                  <v-btn
                    color="primary"
                    class="action-btn me-2"
                    :loading="connectionLoading"
                    @click="respondRequest('accept')"
                  >
                    <v-icon left>
                      {{ mdiCheck }}
                    </v-icon>
                    Accept
                  </v-btn>
                  <v-btn
                    outlined
                    class="action-btn"
                    :loading="connectionLoading"
                    @click="respondRequest('reject')"
                  >
                    <v-icon left>
                      {{ mdiClose }}
                    </v-icon>
                    Decline
                  </v-btn>
                </div>

                <!-- Connected -->
                <v-menu
                  v-else-if="connectionStatus === 'accepted'"
                  bottom
                  left
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      color="success"
                      class="action-btn"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon left>
                        {{ mdiAccountCheck }}
                      </v-icon>
                      Connected
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item @click="removeConnection()">
                      <v-list-item-icon>
                        <v-icon
                          size="20"
                          color="error"
                        >
                          {{ mdiAccountRemove }}
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="error--text">
                          Remove Connection
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>

              <!-- Message button (always show) -->
              <v-btn
                outlined
                class="action-btn ms-2"
                @click="messageSnackbar = true"
              >
                <v-icon left>
                  {{ mdiMessage }}
                </v-icon>
                Message
              </v-btn>
            </div>
          </div>
        </v-container>
      </div>

      <!-- Navigation Tabs -->
      <v-container class="profile-tabs-container">
        <v-card class="mb-4">
          <v-tabs
            :value="activeTab"
            show-arrows
          >
            <v-tab @click="navigateTo('public_user')">
              All
            </v-tab>
            <v-tab @click="navigateTo('public_pictures')">
              Pictures
            </v-tab>
            <v-tab @click="navigateTo('public_biography')">
              Biography
            </v-tab>
          </v-tabs>
        </v-card>
      </v-container>

      <v-snackbar
        v-model="messageSnackbar"
        :timeout="3000"
        top
      >
        Messaging feature coming soon!
        <template v-slot:action="{ attrs }">
          <v-btn
            text
            v-bind="attrs"
            @click="messageSnackbar = false"
          >
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import {
  mdiMessage,
  mdiAccountOff,
  mdiAccountPlus,
  mdiAccountClock,
  mdiAccountCheck,
  mdiAccountRemove,
  mdiCheck,
  mdiClose,
} from '@mdi/js'

export default {
  name: 'PublicProfileHeader',
  props: {
    activeTab: {
      type: Number,
      default: 0,
    },
  },
  data: () => ({
    mdiMessage,
    mdiAccountOff,
    mdiAccountPlus,
    mdiAccountClock,
    mdiAccountCheck,
    mdiAccountRemove,
    mdiCheck,
    mdiClose,

    loading: true,
    userFound: false,
    messageSnackbar: false,

    user: {
      name: '',
      username: '',
      bio: '',
      avatar: '',
      created_at: '',
    },
    connections: [],
    connectionsCount: 0,

    // Connection status with current user
    connectionStatus: 'none', // none, pending, accepted, rejected
    connectionDirection: '', // incoming, outgoing
    connectionId: null,
    connectionLoading: false,

    endpoint: process.env.VUE_APP_ROOT_API,
    defaultAvatar: require('@/assets/images/avatars/1.png'),
  }),
  computed: {
    username() {
      return this.$route.params.username
    },
    avatarUrl() {
      if (this.user.avatar) {
        return `https://self-biography-system.s3.us-east-2.amazonaws.com/${this.user.avatar}`
      }

      return this.defaultAvatar
    },
    isLoggedIn() {
      return !!this.$store.state.jwtToken
    },
    isSelf() {
      const currentUser = this.$store.state.user
      if (!currentUser || !currentUser.extra_data) return false

      return currentUser.extra_data.username === this.username
    },
  },
  mounted() {
    this.loadUser()
  },
  methods: {
    getAuthHeaders() {
      const token = this.$store.state.jwtToken
      if (token) {
        return { Authorization: token }
      }

      return {}
    },

    getAvatarUrl(avatar) {
      if (avatar) {
        return `https://self-biography-system.s3.us-east-2.amazonaws.com/${avatar}`
      }

      return this.defaultAvatar
    },

    async loadUser() {
      this.loading = true
      try {
        const userResponse = await axios.get(
          `${this.endpoint}/getPublicUser?username=${encodeURIComponent(this.username)}`,
          { headers: this.getAuthHeaders() },
        )

        if (userResponse.data.content && userResponse.data.content.length) {
          this.user = userResponse.data.content[0]
          this.userFound = true
          this.$emit('user-loaded', this.user)

          // Load connections and connection status in parallel
          this.loadPublicConnections()
          if (this.isLoggedIn && !this.isSelf) {
            this.loadConnectionStatus()
          }
        } else {
          this.userFound = false
        }
      } catch {
        this.userFound = false
      }
      this.loading = false
    },

    async loadPublicConnections() {
      try {
        const response = await axios.get(
          `${this.endpoint}/getPublicConnections?username=${encodeURIComponent(this.username)}`,
        )
        this.connections = response.data.connections || []
        this.connectionsCount = response.data.count || 0
        this.$emit('connections-loaded', {
          connections: this.connections,
          count: this.connectionsCount,
        })
      } catch {
        // Silent fail
      }
    },

    async loadConnectionStatus() {
      try {
        const response = await axios.get(
          `${this.endpoint}/getConnectionStatus?username=${encodeURIComponent(this.username)}`,
          { headers: this.getAuthHeaders() },
        )
        this.connectionStatus = response.data.status || 'none'
        this.connectionDirection = response.data.direction || ''
        this.connectionId = response.data.connection_id || null
      } catch {
        this.connectionStatus = 'none'
      }
    },

    async sendRequest() {
      this.connectionLoading = true
      try {
        await axios.post(
          `${this.endpoint}/sendConnectionRequest`,
          { username: this.username },
          { headers: this.getAuthHeaders() },
        )
        this.connectionStatus = 'pending'
        this.connectionDirection = 'outgoing'
      } catch {
        // Silent fail
      }
      this.connectionLoading = false
    },

    async respondRequest(action) {
      this.connectionLoading = true
      try {
        await axios.post(
          `${this.endpoint}/handleConnectionRequest`,
          { connectionId: this.connectionId, action },
          { headers: this.getAuthHeaders() },
        )
        if (action === 'accept') {
          this.connectionStatus = 'accepted'
          this.loadPublicConnections()
        } else {
          this.connectionStatus = 'none'
        }
      } catch {
        // Silent fail
      }
      this.connectionLoading = false
    },

    async cancelRequest() {
      this.connectionLoading = true
      try {
        await axios.post(
          `${this.endpoint}/removeConnection`,
          { connectionId: this.connectionId },
          { headers: this.getAuthHeaders() },
        )
        this.connectionStatus = 'none'
        this.connectionId = null
      } catch {
        // Silent fail
      }
      this.connectionLoading = false
    },

    async removeConnection() {
      this.connectionLoading = true
      try {
        await axios.post(
          `${this.endpoint}/removeConnection`,
          { connectionId: this.connectionId },
          { headers: this.getAuthHeaders() },
        )
        this.connectionStatus = 'none'
        this.connectionId = null
        this.loadPublicConnections()
      } catch {
        // Silent fail
      }
      this.connectionLoading = false
    },

    navigateTo(routeName) {
      if (this.$route.name !== routeName) {
        this.$router.push({
          name: routeName,
          params: { username: this.username },
          query: { ...this.$route.query },
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.profile-header {
  position: relative;
  background: #1b1b2f;
}

.profile-header-content {
  position: relative;
  padding-bottom: 0;
}

.profile-header-row {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 16px;
}

.profile-avatar-wrapper {
  flex-shrink: 0;
}

.profile-avatar {
  border: 4px solid #1e1e2d;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.profile-info {
  flex: 1;
  min-width: 200px;
  padding-bottom: 8px;
}

.profile-name {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.2;
  color: #fff;
  margin-bottom: 2px;
}

.profile-friends-count {
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 4px;
  font-size: 0.95rem;
}

.profile-friends-avatars {
  display: flex;
  gap: 0;

  .friend-avatar {
    border: 2px solid #1e1e2d;
    margin-left: -6px;

    &:first-child {
      margin-left: 0;
    }
  }
}

.profile-actions {
  padding-bottom: 12px;
  display: flex;
  align-items: center;
}

.action-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
}

.profile-tabs-container {
  padding-top: 24px;
  padding-bottom: 0;
}

@media (max-width: 600px) {
  .profile-avatar {
    &.v-avatar {
      height: 120px !important;
      width: 120px !important;
      min-width: 120px !important;
    }
  }

  .profile-name {
    font-size: 1.5rem;
  }

  .profile-header-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .profile-actions {
    width: 100%;

    .action-btn {
      flex: 1;
    }
  }
}
</style>
